import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import { fakeTableList, headList, schema, stepper } from "./parts";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { FacilityKind, Status } from "../../assets/data/Constants";
import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../context/globalContext";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { BusinessContext } from "../../context/businessContext";
import TkTable from "../../components/atoms/TkTable";
import TkStepper from "../../components/atoms/TkStepper";
import { httpAPI_APP005, httpAPI_APP006 } from "./api/request";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import _ from "lodash";

/**
 * SCRC060114 共済契約対象施設への復帰情報
 * @returns SCRC060114 共済契約対象施設への復帰情報
 */
const SCRC060114 = () => {
  const navigate = useNavigate();
  // 認証情報
  const { state, setState } = useContext(GlobalContext);
  // 持回情報
  const { state: businessState } = useContext(BusinessContext);
  const [stateInfoTable, setStateInfoTable] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [staffTableList, setStaffTableList] = useState<any>({
    infoHeadTitleList: headList,
    infoBodyList: [],
  });
  const latestApplyStatus = businessState?.job_28?.latestApplyStatus;
  const btnDisabled =
    latestApplyStatus !== Status["届出待ち"] &&
    latestApplyStatus !== Status["差戻し一時保存"];

  // ユーザタイプ
  const userType = state.authInfo?.userType;
  // ユーザタイプが機構か
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isUserTypeKiko
    ? businessState.common?.contractorNo!
    : state.authInfo?.contractorNo!;

  const handleInit = async () => {
    // 共済契約対象施設への復帰情報参照API 呼び出し
    try {
      const res = await httpAPI_APP005({
        contractorNo: contractorNo,
        staffNo: businessState.job_28?.staffNo,
        applyId: businessState.job_28?.applyId,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;
        // 職員番号(職員情報)
        // 職員名漢字(職員情報)
        // 職員氏名(職員情報)
        // 生年月日(職員情報)
        const staffList = [
          [
            {
              children: res.staffNo,
              align: "left",
            },
            {
              children: res.staffNameKana,
              align: "left",
            },
            {
              children: res.staffNameKanji,
              align: "left",
            },
            {
              children: res.staffBirthday,
              align: "center",
            },
          ],
        ];
        setStaffTableList({
          infoHeadTitleList: headList,
          infoBodyList: staffList,
        });
        // 施設番号
        let facilityNo = resArr.find((item) => item.keyword === "facilityNo")!;
        facilityNo.text = res.facilityNo;
        // 施設名
        let facilityName = resArr.find(
          (item) => item.keyword === "facilityName"
        )!;
        facilityName.text = res.facilityName;
        // 種類
        let facilityKind = resArr.find(
          (item) => item.keyword === "facilityKind"
        )!;
        facilityKind.text =
          FacilityKind[res.facilityKind as keyof typeof FacilityKind];
        // 共済契約対象外施設等異動年月日(異動/復帰年月日)
        let changeDate = resArr.find((item) => item.keyword === "changeDate")!;
        changeDate.text = res.transferDate;
        // 共済契約対象施設等復帰年月日(異動/復帰年月日)
        let returnDate = resArr.find((item) => item.keyword === "returnDate")!;
        returnDate.text = res.returnDate;
        // 職種(復帰時の職種・本俸月額)
        let jobCd = resArr.find((item) => item.keyword === "jobCd")!;
        jobCd.setDefaultValue = res.returnJobCd;
        jobCd.disabled = btnDisabled;

        // 復帰時の俸給表の額(復帰時の職種・本俸月額)
        let salaryTableAmt = resArr.find(
          (item) => item.keyword === "salaryTableAmt"
        )!;
        salaryTableAmt.setDefaultValue = res.returnMonthlySalaryAmt;
        salaryTableAmt.disabled = btnDisabled;
        // 復帰時の俸給の調整額(復帰時の職種・本俸月額)
        let salaryAdjustmentAmt = resArr.find(
          (item) => item.keyword === "salaryAdjustmentAmt"
        )!;
        salaryAdjustmentAmt.setDefaultValue = res.returnSalaryAdjustmentAmt;
        salaryAdjustmentAmt.disabled = btnDisabled;

        setStateInfoTable([...resArr]);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCheckAndSubmit = async (data: any) => {
    if (data.salaryTableAmt <= 73999) {
      // 入出力項目.俸給表の額 ≦ 73,999円
      setState({
        ...state,
        errorMsgId: "TKMSG_SCR0012E",
      });
      return;
    }
    // 共済契約対象施設への復帰情報更新API 呼び出し
    try {
      const res = await httpAPI_APP006({
        applyId: businessState.job_28?.applyId ?? 0,
        staffNo: businessState.job_28?.staffNo ?? "",
        returnJobCd: data?.jobCd,
        returnSalaryTableAmt: data?.salaryTableAmt,
        returnSalaryAdjustmentAmt: data?.salaryAdjustmentAmt,
        contractorNo: contractorNo,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRC060013");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC060114" data-testid="SCRC060114PageForTest">
      <TkStepper steps={stepper} activeStep={btnDisabled ? 1 : 0} />

      <TkInfoManual rule={"\\n"}>{labelids.labelid.LBLC060114_01}</TkInfoManual>

      {isUserTypeKiko && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={`${businessState.common?.contractorNo}:${businessState.common?.contractorName}`}
        />
      )}

      <Box className="tkFakeTableLineTitleText">職員情報</Box>
      <TkTable
        headList={staffTableList.infoHeadTitleList}
        bodyList={staffTableList.infoBodyList}
      />

      <TkFakeTable
        list={stateInfoTable}
        ruleSchema={schema}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          {
            submitButtonText: "登録",
            showButton: !btnDisabled,
          },
        ]}
        onCheckAndSubmit={(data: any) => onCheckAndSubmit(data)}
      />
    </Box>
  );
};

export default SCRC060114;
